import React, { useEffect, FC, useState, useMemo } from "react";
import { Modal, ModalBody, Form, Row, Alert, Col, Button } from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import { Icon, TooltipComponent } from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import FormMultiSelect from "../form-components/MultiSelectComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormTextArea from "../form-components/TextAreaComponent";
import { Popover } from "antd";
import deliveryAddressApis from "../../api/master/deliveryAddress";
import { nanoid } from "nanoid";
import dayjs from "dayjs";
import supplierApis from "../../api/master/supplier";
import brandMasterApis from "../../api/master/brandMaster";
import Swal from "sweetalert2";
interface SelectOptions {
  label: string;
  value: any;
}

type Cess = {
  cessName: string;
  cessType?: string;
  cessPercentage?: string;
  cessAmount?: string;
};

export const TAX_SLAP_DROPDOWN = [
  {
    label: "0%",
    value: "0",
  },
  {
    label: "5%",
    value: "1",
  },
  {
    label: "12%",
    value: "2",
  },
  {
    label: "18%",
    value: "3",
  },
  {
    label: "28%",
    value: "4",
  },
];

const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  taxConfiguration,
  needByDateType,
  deliveryAddressType,
  status,
  materialCategoryOptions,
  materialsList,
  siteId,
  segmentLevel,
  isTransferPoContext,
  boqData = [],
}) => {
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);

  const [makeOptions, setMakeOptions] = useState<SelectOptions[]>([]);
  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const [qtyExceededError, setQtyExceededError] = useState<string>("");

  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  const files = watch("attachment");
  const siteIdWatch = watch("siteId");


  const materialNameWatch = watch("materialName");
  const quantityWatch = watch("quantity");

  const matchedBoqItem = useMemo(() => {
    if (!Array.isArray(boqData) || !boqData.length || !materialNameWatch) return null;
    return boqData.find(
      (boq: any) =>
        (boq.materialId?.toString() || boq.materialID?.toString()) ===
        materialNameWatch?.toString()
    ) || null;
  }, [boqData, materialNameWatch]);

  const prRemainingQty = useMemo(() => {
    if (!matchedBoqItem) return null;
    const qty = parseFloat(matchedBoqItem.prRemainingQty);
    return isNaN(qty) ? null : qty;
  }, [matchedBoqItem]);

  const initialQty = useMemo(() => {
    if (formData?.action === "edit" && formData?.item?.quantity != null) {
      return parseFloat(formData.item.quantity) || 0;
    }
    return 0;
  }, [formData?.action, formData?.item?.quantity]);

  const maxAllowedQty = useMemo(() => {
    if (prRemainingQty == null) return null;
    const val = prRemainingQty + initialQty;
    return val > 0 ? val : 0;
  }, [prRemainingQty, initialQty]);

  useEffect(() => {
    if (maxAllowedQty != null) {
      const enteredQty = parseFloat(quantityWatch) || 0;
      if (enteredQty > initialQty && enteredQty > maxAllowedQty) {
        setQtyExceededError(`Max allowed: ${maxAllowedQty}`);
      } else {
        setQtyExceededError("");
      }
    } else {
      setQtyExceededError("");
    }
  }, [quantityWatch, maxAllowedQty, initialQty]);




  // Generate dynamic tax options including rate contract tax percentage
  const taxDropdownOptions = useMemo(() => {
    const baseOptions = [...TAX_SLAP_DROPDOWN];

    // If editing an item with tax percentages, check if we need to add custom tax rate
    if (formData?.item && modal) {
      const item = formData.item;

      if (taxConfiguration?.gstType === "IGST") {
        // For IGST, use the full igstPercentage value
        const igstPercentage = Number(item.igstPercentage || 0);
        if (igstPercentage > 0) {
          const taxPercentageStr = igstPercentage.toString();
          const exists = baseOptions.some(
            (opt: any) =>
              opt.label === `${taxPercentageStr}%` ||
              (parseFloat(opt.label.replace("%", "")) === igstPercentage)
          );

          if (!exists) {
            const label =
              igstPercentage % 1 === 0
                ? `${igstPercentage}%`
                : `${igstPercentage.toFixed(2)}%`;
            baseOptions.push({
              label: label,
              value: `custom_${igstPercentage}`,
              taxRate: igstPercentage,
              isCustom: true,
            } as any);
          }
        }
      } else {
        // For CGST/SGST, combine the percentages
        const cgstPercentage = Number(item.cgstPercentage || 0);
        const sgstPercentage = Number(item.sgstPercentage || 0);
        const combinedPercentage = cgstPercentage + sgstPercentage;

        if (combinedPercentage > 0) {
          const taxPercentageStr = combinedPercentage.toString();
          const exists = baseOptions.some(
            (opt: any) =>
              opt.label === `${taxPercentageStr}%` ||
              (parseFloat(opt.label.replace("%", "")) === combinedPercentage)
          );

          if (!exists) {
            const label =
              combinedPercentage % 1 === 0
                ? `${combinedPercentage}%`
                : `${combinedPercentage.toFixed(2)}%`;
            baseOptions.push({
              label: label,
              value: `custom_${combinedPercentage}`,
              taxRate: combinedPercentage,
              isCustom: true,
            } as any);
          }
        }
      }
    }

    return baseOptions;
  }, [formData?.item, modal, taxConfiguration?.gstType]);

  useEffect(() => {
    if (files && files[0]?.size && files[0].size > 2097152) {
      setValue("attachment", null);
      setAlertInfo({
        type: "error",
        message: "File size should be less than 2 MB",
        errors: [],
      });
    }
  }, [files]);

  const materialCategory = watch("materialCategory");
  const materialName = watch("materialName");
  const deliveryAddressId = watch("deliveryAddressId");
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const fetchDeliveryAddress = async () => {
    try {
      const { data, status, message } =
        await deliveryAddressApis.fetchAddressBySite(siteId);
      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      } else {
        console.log(message);
      }
    } catch (error) {
      console.log("Error fetching delivery addresses:", error);
    }
  };

  useEffect(() => {
    if (siteId) fetchDeliveryAddress();
  }, [siteId]);

  const fetchMakeOptions = async (materialId: any) => {
    if (!materialId) {
      setMakeOptions([]);
      return;
    }
    try {
      const response = await brandMasterApis.fetchBrandById(materialId);
      if (response?.status && response?.data?.brands) {
        const options = response.data.brands
          .filter((brand: any) => brand.status?.toUpperCase() === "ACTIVE")
          .map((brand: any) => ({
            label: brand.make,
            value: brand.id,
          }));
        setMakeOptions(options);
      } else {
        setMakeOptions([]);
      }
    } catch (error) {
      console.log("Failed to fetch make options", error);
      setMakeOptions([]);
    }
  };

  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        if (
          (formData.action === "add" || formData.index === null) &&
          needByDateType == "Order Level" &&
          deliveryAddressType == "Order Level" &&
          !(segmentLevel && siteId)
        ) {
          if (Array.isArray(materialOption)) {
            const filteredOptions = materialOption.filter(
              (option) =>
                !materialsList || !Array.isArray(materialsList) || !materialsList.some((row: any) => row?.materialId?.toString() === option.value)
            );
            setMaterialOptions(filteredOptions);
          }
        } else {
          setMaterialOptions(materialOption);
        }
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };


  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    if (!modal || !formData.item) {
      return;
    }

    const {
      categoryId,
      materialId,
      hsnCode,
      quantity,
      quantityInvoiced,
      quantityTransferred,
      originalQuantityTransferred,
      uomId,
      unitPrice,
      cgstPercentage,
      sgstPercentage,
      igstPercentage,
      deliveryAddressId,
      description,
      needByDate,
      file,
    } = formData.item;

    setValue("materialCategory", categoryId);
    (async () => {
      const _materialData = await fetchMaterials(categoryId, materialId);
      await Promise.all([
        fetchUom(materialId, _materialData, uomId),
        fetchMakeOptions(materialId),
      ]);
      if (formData.item?.makeInfo?.length > 0) {
        setValue("make", formData.item.makeInfo.map((m: any) => Number(m.makeId)));
      }
    })();
    setValue("hsnCode", hsnCode);
    setValue("description", description);
    setValue("attachment", file);

    // In Transfer PO context, when editing, use Available Qty (Qty - Invoiced Qty - Original Transferred Qty)
    let quantityToSet = Number(quantity);
    if (isTransferPoContext && formData.action === "edit") {
      const qty = Number(quantity) || 0;
      const invoicedQty = Number(quantityInvoiced) || 0;
      // Use originalQuantityTransferred if available (for calculation), otherwise use quantityTransferred
      const originalTransferredQty = Number(originalQuantityTransferred !== undefined ? originalQuantityTransferred : quantityTransferred) || 0;
      const availableQty = qty - invoicedQty - originalTransferredQty;
      quantityToSet = availableQty > 0 ? availableQty : 0;
    }
    setValue("quantity", quantityToSet);
    setValue("unitPrice", Number(unitPrice));
    setValue("deliveryAddressId", deliveryAddressId);
    setValue("needByDate", dayjs(needByDate));

    if (taxConfiguration?.gstType == "IGST") {
      // For IGST, use the full igstPercentage value
      const igstPercentageValue = Number(igstPercentage || 0);
      if (igstPercentageValue > 0) {
        const slab = taxDropdownOptions.find(
          (item: any) => {
            const itemRate = item.taxRate || parseFloat(item.label.replace("%", ""));
            return Math.abs(itemRate - igstPercentageValue) < 0.01;
          }
        );
        if (slab) {
          setValue("igst", slab.value);
        } else {
          // If not found, create a custom value
          const customValue = `custom_${igstPercentageValue}`;
          setValue("igst", customValue);
        }
      }
    } else {
      // For CGST/SGST, combine the percentages and show combined value
      const cgstPercentageValue = Number(cgstPercentage || 0);
      const sgstPercentageValue = Number(sgstPercentage || 0);
      const combinedPercentage = cgstPercentageValue + sgstPercentageValue;

      if (combinedPercentage > 0) {
        const slab = taxDropdownOptions.find(
          (item: any) => {
            const itemRate = item.taxRate || parseFloat(item.label.replace("%", ""));
            return Math.abs(itemRate - combinedPercentage) < 0.01;
          }
        );
        if (slab) {
          setValue("cgst", slab.value);
          setValue("sgst", slab.value);
        } else {
          // If not found, create a custom value
          const customValue = `custom_${combinedPercentage}`;
          setValue("cgst", customValue);
          setValue("sgst", customValue);
        }
      }
    }

    setQtyExceededError("");
  }, [modal, formData.item, segmentLevel, taxConfiguration, setValue, taxDropdownOptions, isTransferPoContext]);

  const submitForm = (data: any) => {
    const {
      materialCategory,
      materialName,
      hsnCode,
      quantity,
      unitPrice,
      igst,
      cgst,
      description,
      uomId,
      make,
      needByDate,
      attachment,
      deliveryAddressId,
    } = data;

    // Validate quantity against prRemainingQty from BOQ data
    if (Array.isArray(boqData) && boqData.length > 0) {
      const matched = boqData.find(
        (boq: any) =>
          (boq.materialId?.toString() || boq.materialID?.toString()) ===
          materialName?.toString()
      );
      if (matched) {
        const enteredQty = parseFloat(quantity) || 0;
        const remainingQty = parseFloat(matched.prRemainingQty) || 0;
        const savedQty = formData?.action === "edit" ? (parseFloat(formData?.item?.quantity) || 0) : 0;
        const maxAllowed = Math.max(remainingQty + savedQty, 0);
        if (enteredQty > savedQty && enteredQty > maxAllowed) {
          setQtyExceededError(`Max allowed: ${maxAllowed}`);
          Swal.fire({
            icon: "error",
            title: "Quantity Exceeded",
            text: `Entered quantity exceeds the maximum allowed quantity (${maxAllowed}). Please adjust the quantity.`,
          });
          return;
        }
      }
    }
    setQtyExceededError("");

    let _cess: Cess[] = [];
    let cessTotal = 0;
    taxConfiguration?.cess?.forEach((cessItem: any, index: any) => {
      if (!cessItem.cessName) return;
      const cessPercentage = data[cessItem.cessName];
      const cessAmount = (cessPercentage * quantity * unitPrice) / 100;
      cessTotal += cessAmount;
      _cess.push({
        cessName: cessItem.cessName,
        cessType: cessItem.cessType,
        cessPercentage,
        cessAmount: cessAmount?.toString(),
      });
    });
    const item = {
      materialName,
      hsnCode,
      quantity,
      unitPrice,
      amount: quantity * unitPrice,
      description,
      igst: 0,
      igstPercentage: 0,
      igstAmount: 0,
      cgst: 0,
      cgstPercentage: 0,
      cgstAmount: 0,
      sgst: 0,
      sgstPercentage: 0,
      sgstAmount: 0,
      lineTotal: 0,
    };
    item.igst = 0;
    item.igstAmount = 0;
    item.igstPercentage = 0;
    item.cgst = 0;
    item.cgstAmount = 0;
    item.cgstPercentage = 0;
    item.sgst = 0;
    item.sgstAmount = 0;
    item.sgstPercentage = 0;

    // Helper function to extract tax rate from dropdown value (handles custom_ prefix)
    const getTaxRate = (taxValue: any, dropdown: any[]) => {
      if (!taxValue) return null;
      const taxStr = taxValue.toString();

      // Check if it's a custom value (custom_6 format)
      if (taxStr.startsWith("custom_")) {
        const rate = parseFloat(taxStr.replace("custom_", ""));
        return isNaN(rate) ? null : rate;
      }

      // Find in dropdown
      const found = dropdown.find((item) => item.value === taxStr);
      if (found) {
        // Extract from label or use taxRate if available
        if (found.taxRate) return found.taxRate;
        const rate = parseFloat(found.label.replace("%", ""));
        return isNaN(rate) ? null : rate;
      }

      return null;
    };

    let selectedTax;
    switch (taxConfiguration?.taxType) {
      case "IGST":
        item.igst = igst;
        const igstRate = getTaxRate(igst, taxDropdownOptions);
        if (igstRate !== null) {
          item.igstPercentage = igstRate; // Full percentage for IGST
          item.igstAmount = (igstRate * item.amount) / 100;
        } else {
          item.igstPercentage = 0;
          item.igstAmount = 0;
        }
        break;
      case "CGST":
        item.cgst = cgst;
        item.sgst = cgst;
        const cgstRate = getTaxRate(cgst, taxDropdownOptions);
        if (cgstRate !== null) {
          // Split by 2 for CGST+SGST
          item.cgstPercentage = cgstRate / 2;
          item.sgstPercentage = cgstRate / 2;
          item.cgstAmount = (item.cgstPercentage * item.amount) / 100;
          item.sgstAmount = (item.sgstPercentage * item.amount) / 100;
        } else {
          item.cgstPercentage = 0;
          item.sgstPercentage = 0;
          item.cgstAmount = 0;
          item.sgstAmount = 0;
        }
        break;
      default:
        break;
    }

    if (igst) {
      item.igst = igst;
      const igstRateValue = getTaxRate(igst, taxDropdownOptions);
      if (igstRateValue !== null) {
        item.igstPercentage = igstRateValue; // Full percentage for IGST
        item.igstAmount = (igstRateValue * item.amount) / 100;
      } else {
        item.igstPercentage = 0;
        item.igstAmount = 0;
      }
    }
    if (cgst) {
      item.cgst = cgst;
      item.sgst = cgst;
      const cgstRateValue = getTaxRate(cgst, taxDropdownOptions);
      if (cgstRateValue !== null) {
        // Split by 2 for CGST+SGST
        item.cgstPercentage = cgstRateValue / 2;
        item.sgstPercentage = cgstRateValue / 2;
        item.cgstAmount = (item.cgstPercentage * item.amount) / 100;
        item.sgstAmount = (item.sgstPercentage * item.amount) / 100;
      } else {
        item.cgstPercentage = 0;
        item.sgstPercentage = 0;
        item.cgstAmount = 0;
        item.sgstAmount = 0;
      }
    }

    item.lineTotal =
      item.amount +
      item.igstAmount +
      item.cgstAmount +
      item.sgstAmount +
      cessTotal;

    const materialLabel = materialOptions.find(
      (item) => item.value == materialName
    )?.label;
    const uomLabel = uomOptions.find((item) => item.value == uomId)?.label;
    const selectedMakeIds = Array.isArray(make) ? make : [];
    const makeInfoData = selectedMakeIds
      .map((id: any) => ({
        makeId: Number(id),
        makeName: makeOptions.find((item) => item.value == id)?.label || "",
      }))
      .filter((m: any) => m.makeId && m.makeName);
    const formResult: any = {
      categoryId: materialCategory,
      materialId: materialName,
      materialName: materialLabel,
      hsnCode,
      uomId,
      uom: uomLabel,
      makeInfo: makeInfoData,
      description,
      file: attachment,
      quantity,
      unitPrice,
      deliveryAddressId,
      needByDate: dayjs(needByDate).format("DD-MMM-YYYY"),
      uomShortCode: uomLabel,
      amount: item.amount.toString(),
      // igst: item.igst.toString(),
      igstPercentage: item.igstPercentage.toString(),
      igst: item.igstAmount.toString(),
      // cgst: item.cgst.toString(),
      cgstPercentage: item.cgstPercentage.toString(),
      cgst: item.cgstAmount.toString(),
      // sgst: item.sgst.toString(),
      sgstPercentage: item.sgstPercentage.toString(),
      sgst: item.sgstAmount.toString(),
      cess: _cess,
      grossTotal: item.lineTotal,
      attachment: attachment?.length ? true : false,
    };

    if (formData.action == "add" || !formData.index) {
      append({
        uuid: nanoid(),
        lineNo: null,
        ...formResult,
      });
    } else {
      // Preserve original values when updating in Transfer PO context
      const updateData: any = {
        uuid: formData.index,
        lineNo: formData?.item?.lineNo,
        ...formResult,
      };

      // In Transfer PO context, preserve quantityInvoiced and originalQuantityTransferred
      if (isTransferPoContext) {
        // Preserve original quantity (not the available quantity shown in form)
        updateData.quantity = Number(formData.item.quantity) || 0;
        // Preserve quantityInvoiced
        updateData.quantityInvoiced = Number(formData.item.quantityInvoiced) || 0;
        // Preserve originalQuantityTransferred
        if (formData.item.originalQuantityTransferred !== undefined) {
          updateData.originalQuantityTransferred = Number(formData.item.originalQuantityTransferred) || 0;
        }
        // Update quantityTransferred with the new quantity value (available qty that user edited)
        updateData.quantityTransferred = Number(quantity) || 0;
      }

      update(formData.index, updateData);
    }
    closeModal();
  };
  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }
    let address = options?.find((item: any) => item[matchKey] == id);
    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city}, {address?.stateName}, {address?.countryName} -{" "}
              {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };

  const isDissabled = (keyName: string) => {
    // In Transfer PO context, allow editing of quantity, unitPrice, and hsnCode
    if (isTransferPoContext && formData.action === "edit") {
      if (["quantity", "unitPrice", "hsnCode"].includes(keyName)) {
        return false; // Enable these fields in Transfer PO context
      }
    }

    // If item is from Rate Contract conversion, disable specific fields
    const rateContractDisabledFields = [
      "materialCategory",
      "materialName",
      "hsnCode",
      "quantity",
      "uomId",
      "unitPrice",
      "igst",
      "cgst",
      "sgst",
    ];

    // Check if cess fields should be disabled
    const isCessField = taxConfiguration?.cess?.some((cess: any) => cess.cessName === keyName);

    // Check for rateContractLineId or rateContractPriceId
    // But allow editing in Transfer PO context even for rate contract items
    if ((formData.item?.rateContractLineId || formData.item?.rateContractPriceId) && !isTransferPoContext) {
      if (isCessField || rateContractDisabledFields.includes(keyName)) {
        return true;
      }
    }

    if (formData.action == "add" || formData.index == null) {
      return false;
    } else if (formData.action == "edit") {
      // In Transfer PO context, always allow editing of quantity, unitPrice, and hsnCode
      if (isTransferPoContext && ["quantity", "unitPrice", "hsnCode"].includes(keyName)) {
        return false;
      }

      switch (status) {
        case "pending":
          return false;
        case "Pending Approval":
          return false;
        case "Approved":
          if (["quantity", "unitPrice", "needByDate"].includes(keyName))
            return false;
          return true;
        case "Change request accepted by Buyer":
          if (["quantity", "unitPrice", "needByDate"].includes(keyName))
            return false;
          return true;
        case "Rejected from Buyer":
          if (["quantity", "unitPrice", "needByDate"].includes(keyName))
            return false;
          return true;
        case "To be Invoiced":
          if (["quantity", "unitPrice", "needByDate"].includes(keyName))
            return false;
          return true;
        case "Changes from Buyer":
          if (["quantity", "unitPrice", "needByDate"].includes(keyName))
            return false;
          return true;
        case "Rejected":
          return false;
        default:
          // In Transfer PO context, allow editing of quantity, unitPrice, and hsnCode for any status
          if (isTransferPoContext && ["quantity", "unitPrice", "hsnCode"].includes(keyName)) {
            return false;
          }
          return true;
      }
    }
  };


  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mt-12">
              <div className="col-md-4">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptions}
                  placeholder="Select Material Category"
                  disabled={isDissabled("materialCategory")}
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                    setMakeOptions([]);
                    setValue("make", []);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions}
                  placeholder="Select Material Name"
                  disabled={isDissabled("materialName")}
                  onChange={async (e: any) => {
                    console.log(materialData);
                    setValue("make", []);
                    await Promise.all([
                      fetchUom(e, materialData, null),
                      fetchMakeOptions(e),
                    ]);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  type="number"
                  // required={true}
                  name="hsnCode"
                  label="HSN/SAC"
                  pattern={/^$|^[0-9]{4,8}$/}
                  placeholder="Enter HSN"
                  disabled={isDissabled("hsnCode")}
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-4">
                <FormMultiSelect
                  name="make"
                  label="Make"
                  options={makeOptions}
                  placeholder="Select Make"
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-2">
                <FormInput
                  type="number"
                  required={true}
                  name="quantity"
                  min={0.1}
                  label="Quantity"
                  placeholder="Enter Quantity"
                  disabled={isDissabled("quantity")}
                />
                {qtyExceededError && (
                  <div style={{ color: "#ff4d4f", fontSize: 13, marginTop: 4, marginBottom: 4 }}>
                    {qtyExceededError}
                  </div>
                )}
              </div>
              <div className="col-md-2">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                  disabled={isDissabled("uomId")}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  type="number"
                  required={true}
                  min={1}
                  name="unitPrice"
                  label="Price per Unit"
                  placeholder="Enter Price per Unit"
                  disabled={isDissabled("unitPrice")}
                />
              </div>
              {taxConfiguration?.gstType == "IGST" ? (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="igst"
                      label="IGST(%)"
                      required={true}
                      options={taxDropdownOptions}
                      placeholder="Enter IGST"
                      disabled={isDissabled("igst")}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="cgst"
                      label="CGST/SGST(%)"
                      required={true}
                      options={taxDropdownOptions}
                      placeholder="Enter CGST"
                      disabled={isDissabled("cgst")}
                    />
                  </div>
                  <div style={{ marginTop: "15px" }}></div>
                </>
              )}
              {taxConfiguration?.cess?.map((item: any, index: any) => {
                if (!item.cessName) return null;
                return (
                  <>
                    {index === 0 && taxConfiguration?.gstType === "IGST" && (
                      <div style={{ marginTop: "15px" }}></div>
                    )}
                    <div className="col-md-4">
                      <FormInput
                        type="number"
                        required={true}
                        name={item.cessName}
                        label={item.cessName + "(%)"}
                        placeholder={`Enter ${item.cessName}`}
                        disabled={isDissabled(item.cessName)}
                      />
                    </div>
                  </>
                );
              })}
              {needByDateType === "Line Level" && (
                <div className="col-md-4">
                  <FormDatePicker
                    name="needByDate"
                    label="Need By Date"
                    required={true}
                    minDate={new Date()}
                    disabled={isDissabled("needByDate")}
                  />
                </div>
              )}
              <Col md={4}>
                {files && files[0]?.name && (
                  <div className="form-group m-0">
                    <div className="form-label-wrapper">
                      <label className="form-label" htmlFor={"attachment"}>
                        Attachment (If any)
                      </label>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id={`file-tooltip`}
                        text="e.g: Design specificaitons"
                        containerClassName={""}
                      />
                    </div>
                    <div
                      className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                      style={{ borderRadius: "3px" }}
                    >
                      <span>{files[0]?.name}</span>
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: `${files && files[0]?.name ? "none" : "block"}`,
                  }}
                >
                  <FormInput
                    type="file"
                    name="attachment"
                    label="Attachment (If any) "
                    placeholder="Enter Attachment"
                    tooltipText="e.g: Design specificaitons"
                    disabled={isDissabled("attachment")}
                  />
                  {formData?.item?.file && (
                    <div>{formData?.item?.file?.fileName}</div>
                  )}
                </div>
              </Col>

              {deliveryAddressType === "Line Level" && (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="deliveryAddressId"
                      label="Delivery Address"
                      required={true}
                      options={deliveryAddressOptions}
                      disabled={isDissabled("deliveryAddressId")}
                      popOverOptions={
                        <ShowAddressPopOver
                          id={deliveryAddressId}
                          matchKey="id"
                          options={deliveryAddressList}
                          text="Address"
                        />
                      }
                      placeholder={
                        siteIdWatch ? "-Select-" : "Select a site first"
                      }
                      tooltipText="Select the delivery address for the purchase order."
                    />
                  </div>
                </>
              )}
            </Row>
            {/* Site hierarchy fields removed */}
            <Row className="mt-4">
              <div className="col-md-12">
                <FormTextArea
                  name="description"
                  label="Description (if any)"
                  placeholder="Enter Description"
                  minLength={5}
                  maxLength={500}
                  disabled={isDissabled("description")}
                />
              </div>
            </Row>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {(formData.action === "edit" || formData.action == "add") && (
                      <li>
                        <Button
                          className="btn btn-primary btn-md"
                          type="submit"
                          color="primary"
                        >
                          {formData.action == "edit" ? "Update" : "Save"}
                        </Button>
                      </li>
                    )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
